/**
 * General Settings Repository
 * Data abstraction layer for DCS Apps
 */

import { GeneralSettingsApi } from './general-settings-api';
import { CreateDcsAppRequest, DcsApp, DcsAppsListResponse, UpdateDcsAppRequest } from '../types/general-settings-types';

export class GeneralSettingsRepository {
  constructor(private api: GeneralSettingsApi) {}

  /**
   * Fetch DCS Apps for current operator
   * Returns the first app (there should typically be one per operator)
   * @returns Promise<DcsApp | null>
   */
  async getSettings(operatorId?: number): Promise<DcsApp | null> {
    try {
      console.log('[GeneralSettingsRepository] Fetching general settings...');

      const response = await this.api.getDcsApps(operatorId);

      // Get first app (should be the only one for current operator)
      const app = response.rows?.[0];

      if (!app) {
        console.warn('[GeneralSettingsRepository] No DCS app found for operator');
        return null;
      }

      console.log('[GeneralSettingsRepository] Settings fetched successfully:', {
        appId: app.appId,
        operatorId: app.operatorId
      });

      return app;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[GeneralSettingsRepository] Failed to fetch settings:', errorMessage);
      throw new Error(`Failed to fetch general settings - ${errorMessage}`);
    }
  }

  /**
   * Create a new DCS App for an operator (POST /dcs/apps)
   */
  async createSettings(request: CreateDcsAppRequest): Promise<DcsApp> {
    try {
      return await this.api.createDcsApp(request);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to create general settings - ${errorMessage}`);
    }
  }

  /**
   * Update DCS App settings
   * Only sends changed fields (delta)
   * @param app - The app with updated values
   * @param originalApp - The original app (to calculate delta)
   * @returns Promise<void>
   */
  async updateSettings(app: DcsApp, originalApp: DcsApp): Promise<void> {
    try {
      console.log('[GeneralSettingsRepository] Updating settings for app:', {
        appId: app.appId
      });

      // Calculate delta - only include fields that changed
      const delta = this.calculateDelta(app, originalApp);

      if (Object.keys(delta).length === 0) {
        console.log('[GeneralSettingsRepository] No changes detected, skipping update');
        return;
      }

      console.log('[GeneralSettingsRepository] Calculated delta:', {
        appId: app.appId,
        changedFields: Object.keys(delta)
      });

      // Send delta to API, using the app's own operatorId (not the store's cached selection)
      await this.api.updateDcsApp(app.appId, delta, app.operatorId);

      console.log('[GeneralSettingsRepository] Settings updated successfully:', {
        appId: app.appId
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[GeneralSettingsRepository] Failed to update settings:', errorMessage);
      throw new Error(`Failed to update general settings - ${errorMessage}`);
    }
  }

  /**
   * Calculate delta (changed fields) between current and original
   * Only includes fields that have actually changed
   * @private
   */
  private static readonly INCREMENTAL_AUTH_MAP: Record<string, number> = {
    None: 0,
    SingleDuringSettlement: 1,
    Multiple: 2,
  };

  private calculateDelta(current: DcsApp, original: DcsApp): UpdateDcsAppRequest {
    const delta: UpdateDcsAppRequest = { appId: current.appId };

    // Check all fields for changes
    const allKeys = new Set([...Object.keys(current), ...Object.keys(original)]);

    for (const key of allKeys) {
      if (key === 'appId' || key === 'operatorId') {
        // Skip ID fields
        continue;
      }

      const currentValue = (current as any)[key];
      const originalValue = (original as any)[key];

      // Only include if value changed
      if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
        if (key === 'incrementalAuthType' && typeof currentValue === 'string') {
          delta[key] = GeneralSettingsRepository.INCREMENTAL_AUTH_MAP[currentValue] ?? 0;
        } else {
          delta[key] = currentValue;
        }
      }
    }

    console.log('[GeneralSettingsRepository] Delta calculation:', {
      totalFields: allKeys.size,
      changedFields: Object.keys(delta).length - 1 // -1 for appId
    });

    return delta;
  }
}
